import React, { useState, useEffect } from 'react';
import {
  FiWifi,
  FiWifiOff,
  FiRefreshCw,
  FiServer,
  FiMapPin,
  FiHome,
  FiClock,
  FiDownload,
  FiUpload,
  FiEdit2,
  FiHash
} from 'react-icons/fi';
import EditarEquipamento from './EditarEquipamento';
import './StatusAntena.css';

/** Online na UI = último push do MK recente na API (vem da API). WAN ativa/inativa usa status_internet. */
const isAntenaOnline = (eq) =>
  eq.status_antena_online !== undefined && eq.status_antena_online !== null
    ? Boolean(eq.status_antena_online)
    : Boolean(Number(eq.status_internet));

const StatusAntena = ({ equipamentos, loading, onEquipamentoUpdated }) => {
  const [editingEquipamento, setEditingEquipamento] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastApiSync, setLastApiSync] = useState(null);

  useEffect(() => {
    if (!loading && equipamentos) {
      setLastApiSync(new Date());
    }
  }, [equipamentos, loading]);

  const handleRefresh = async () => {
    setRefreshing(true);
    if (onEquipamentoUpdated) {
      await onEquipamentoUpdated();
    }
    setTimeout(() => setRefreshing(false), 1000);
  };

  const formatDate = (value) => {
    if (!value) return 'N/D';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'N/D';
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatMbps = (v) => {
    const n = typeof v === 'number' ? v : parseFloat(v);
    if (!Number.isFinite(n)) return '0.00';
    return n.toFixed(2);
  };

  if (loading) {
    return (
      <div className="status-antena">
        <div className="loading">Carregando equipamentos...</div>
      </div>
    );
  }

  return (
    <div className="status-antena">
      <div className="status-header">
        <div>
          <h1>Status da Antena</h1>
          <p>Monitoramento de equipamentos em tempo real</p>
          {lastApiSync && (
            <p className="status-last-sync">
              Velocidades e status sincronizados às{' '}
              {lastApiSync.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              {' '}(atualização automática a cada ~12s nesta tela)
            </p>
          )}
        </div>
        <button className="refresh-btn" onClick={handleRefresh} disabled={refreshing}>
          <FiRefreshCw className={refreshing ? 'spinning' : ''} /> Atualizar
        </button>
      </div>

      <div className="status-grid">
        {equipamentos && equipamentos.length > 0 ? (
          equipamentos.map((equipamento) => {
            const isOnline = isAntenaOnline(equipamento);
            const rawDbAtivo = Number(equipamento.status_internet) === 1 || equipamento.status_internet === true;
            const offlinePorSinal = rawDbAtivo && !isOnline;
            const wanInativaComSinal = isOnline && !rawDbAtivo;
            const enderecoLinha = [equipamento.endereco, equipamento.numero].filter(Boolean).join(', ');
            return (
              <div
                key={equipamento.id}
                className={`status-card ${isOnline ? 'status-card--online' : 'status-card--offline'}`}
              >
                <div className="status-card__accent" aria-hidden="true" />
                <div className="status-card__inner">
                  <div className="card-header">
                    <div className="status-indicator">
                      <div className={`status-icon-wrap ${isOnline ? 'is-online' : 'is-offline'}`}>
                        {isOnline ? (
                          <FiWifi className="status-icon" aria-hidden />
                        ) : (
                          <FiWifiOff className="status-icon" aria-hidden />
                        )}
                      </div>
                      <div className="status-indicator__text">
                        <span className={`status-badge ${isOnline ? 'online' : 'offline'}`}>
                          {isOnline ? 'Online' : 'Offline'}
                        </span>
                        {offlinePorSinal && (
                          <span className="status-offline-hint" title="Sem push do MikroTik nos últimos minutos">
                            Sem sinal do MK
                          </span>
                        )}
                        {wanInativaComSinal && (
                          <span className="status-offline-hint" title="Script reportou WAN inativa; MK continua enviando para a API">
                            WAN inativa (script)
                          </span>
                        )}
                        {equipamento.server_name && (
                          <span className="status-server-name">{equipamento.server_name}</span>
                        )}
                        {equipamento.mk_ultima_identity && (
                          <span className="status-server-name">
                            Identity MK: {equipamento.mk_ultima_identity}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="card-body">
                    <h3 className="card-title">{equipamento.descricao_mk || 'Equipamento'}</h3>
                    <ul className="card-info">
                      <li className="card-info__row">
                        <span className="card-info__icon" aria-hidden><FiServer /></span>
                        <span className="card-info__label">IP</span>
                        <span className="card-info__value">{equipamento.ip || '—'}</span>
                      </li>
                      <li className="card-info__row">
                        <span className="card-info__icon" aria-hidden><FiMapPin /></span>
                        <span className="card-info__label">Endereço</span>
                        <span className="card-info__value">{enderecoLinha || '—'}</span>
                      </li>
                      {equipamento.bairro && (
                        <li className="card-info__row">
                          <span className="card-info__icon" aria-hidden><FiHome /></span>
                          <span className="card-info__label">Bairro</span>
                          <span className="card-info__value">{equipamento.bairro}</span>
                        </li>
                      )}
                      {equipamento.patrimonio && (
                        <li className="card-info__row">
                          <span className="card-info__icon" aria-hidden><FiHash /></span>
                          <span className="card-info__label">Patrimônio</span>
                          <span className="card-info__value">{equipamento.patrimonio}</span>
                        </li>
                      )}
                      <li className="card-info__row">
                        <span className="card-info__icon" aria-hidden><FiClock /></span>
                        <span className="card-info__label">Último sinal (MK)</span>
                        <span className="card-info__value">
                          {formatDate(equipamento.mk_ultimo_sinal_em || equipamento.ultima_verificacao)}
                        </span>
                      </li>
                    </ul>
                    <div className="card-speeds">
                      <div className="card-speed card-speed--down">
                        <FiDownload className="card-speed__icon" aria-hidden />
                        <div className="card-speed__meta">
                          <span className="card-speed__label">Download</span>
                          <span className="card-speed__value">{formatMbps(equipamento.velocidade_download)} <small>Mbps</small></span>
                        </div>
                      </div>
                      <div className="card-speed card-speed--up">
                        <FiUpload className="card-speed__icon" aria-hidden />
                        <div className="card-speed__meta">
                          <span className="card-speed__label">Upload</span>
                          <span className="card-speed__value">{formatMbps(equipamento.velocidade_upload)} <small>Mbps</small></span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="card-footer">
                    <button
                      type="button"
                      className="edit-btn"
                      onClick={() => setEditingEquipamento(equipamento)}
                    >
                      <FiEdit2 className="edit-btn__icon" aria-hidden />
                      <span>Editar equipamento</span>
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="no-equipamentos">
            <FiWifiOff size={48} />
            <p>Nenhum equipamento cadastrado</p>
          </div>
        )}
      </div>

      {editingEquipamento && (
        <EditarEquipamento
          equipamento={editingEquipamento}
          onClose={() => setEditingEquipamento(null)}
          onEquipamentoUpdated={() => {
            setEditingEquipamento(null);
            if (onEquipamentoUpdated) {
              onEquipamentoUpdated();
            }
          }}
        />
      )}
    </div>
  );
};

export default StatusAntena;
